import zod from 'zod';
import { TotvsIntegrationFormSchema } from '../../../services/IntegrationService/TotvsService/schemas';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import TextFieldComponent from '../../../components/TextFieldComponent';
import { Grid2 } from '@mui/material';
import ButtonComponent from '../../../components/ButtonComponent';
import {
  useCreateTotvsIntegrationAsync,
  useGetTotvsIntegrationByIdAsync,
  useUpdateTotvsIntegrationAsync,
} from '../../../services/IntegrationService/TotvsService/hooks/useMutation';
import CheckboxComponent from '../../../components/CheckboxComponent';
import { useContext, useEffect } from 'react';
import { AppContext } from '../../../context/AppContextProvider';
import { t } from 'i18next';

interface TotvsEditionFormProps {
  totvsIdToUpdate?: number;
  handleSaveForm?: () => void;
}

type TotvsEditionFormInput = zod.infer<typeof TotvsIntegrationFormSchema>;

const TotvsEditionForm = ({
  totvsIdToUpdate,
  handleSaveForm,
}: TotvsEditionFormProps) => {
  const { showSnackbar } = useContext(AppContext);
  const { control, handleSubmit, reset } = useForm<TotvsEditionFormInput>({
    resolver: zodResolver(TotvsIntegrationFormSchema),
    defaultValues: {
      name: '',
      password: '',
      username: '',
      description: '',
      domain: '',
      isActive: true,
    },
  });

  const {
    mutate: mutateCreateTotvsIntegration,
    isLoading: isLoadingCreateTotvsIntegration,
  } = useCreateTotvsIntegrationAsync();
  const {
    mutate: mutateUpdateTotvsIntegration,
    isLoading: isLoadingUpdateTotvsIntegration,
  } = useUpdateTotvsIntegrationAsync();
  const {
    mutateAsync: mutateGetTotvsIntegrationByIdAsync,
    isLoading: isLoadingGetTotvsIntegrationById,
  } = useGetTotvsIntegrationByIdAsync();

  const handleSave = (data: TotvsEditionFormInput) => {
    if (totvsIdToUpdate) {
      mutateUpdateTotvsIntegration(
        { id: totvsIdToUpdate, data },
        {
          onSuccess: () => {
            saveIntegration('Integração totvs atualizada com sucesso.');
          },
        },
      );
      return;
    }
    mutateCreateTotvsIntegration(data, {
      onSuccess: () => {
        saveIntegration('Integração totvs criada com sucesso.');
      },
    });
  };

  const saveIntegration = (message: string) => {
    reset();
    showSnackbar(message, 'success');
    if (handleSaveForm) handleSaveForm();
  };

  useEffect(() => {
    if (totvsIdToUpdate) {
      mutateGetTotvsIntegrationByIdAsync(totvsIdToUpdate).then((r) =>
        reset(r, { keepDefaultValues: true }),
      );
    }
  }, [totvsIdToUpdate]);

  return (
    <>
      <Grid2 container columnSpacing={2}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.totvsForms.name')}
              />
            )}
            name={'name'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.totvsForms.domain')}
              />
            )}
            name={'domain'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.totvsForms.user')}
              />
            )}
            name={'username'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.totvsForms.password')}
              />
            )}
            name={'password'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TextFieldComponent
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
                label={t('integrationsPage.totvsForms.description')}
              />
            )}
            name={'description'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ display: 'flex', justifyContent: 'right' }}>
          <Controller
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <CheckboxComponent
                value={value}
                onChange={onChange}
                label={t('integrationsPage.totvsForms.active')}
              />
            )}
            name={'isActive'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ mt: 2 }}>
          <ButtonComponent
            fullWidth
            onClick={handleSubmit(handleSave)}
            loading={
              isLoadingCreateTotvsIntegration ||
              isLoadingGetTotvsIntegrationById ||
              isLoadingUpdateTotvsIntegration
            }
          >
            {t('integrationsPage.totvsForms.save')}
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </>
  );
};

export default TotvsEditionForm;
